'use client';

import * as React from 'react';
import {
  animate,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useSpring,
  useTransform,
  type MotionValue
} from 'motion/react';

import { AccountMark } from './account-mark';
import { TypedPrompt } from './example-shortlist';

export { AccountMark } from './account-mark';

type AccountRow = { label: string; detail: string; value: string };

// Each connection leads its reveal; the completed composition then rests.
const ARRIVAL_SECONDS = [0.45, 1.1, 1.75] as const;
const SOURCES = ['Company announcement', 'Pricing page', 'CRM record'];
const PEOPLE = [
  {
    photo: 'https://i.pravatar.cc/120?img=47',
    reason: 'Potential executive sponsor',
    x: 12,
    y: 58
  },
  {
    photo: 'https://i.pravatar.cc/120?img=49',
    reason: 'New systems mandate',
    x: 70,
    y: 58
  },
  {
    photo: 'https://i.pravatar.cc/120?img=13',
    reason: 'Prior evaluator',
    x: 44,
    y: 245
  }
] as const;
const ACCOUNT_NODE = { x: 50, y: 145 };

const ACCOUNT_QUESTION = {
  prefix: 'Help me understand ',
  highlights: [{ text: 'Quillstack' }],
  trail: ". What's changed, who matters, and should we reach out now?"
};
const ACCOUNT_QUESTION_TEXT =
  ACCOUNT_QUESTION.prefix +
  ACCOUNT_QUESTION.highlights[0]!.text +
  ACCOUNT_QUESTION.trail;

const BRIEF_ARRIVALS = [0.12, 0.8, 1.45] as const;

export function AccountResearchScene({
  step,
  rows,
  reduced,
  progress
}: {
  step: 'question' | 'brief';
  rows: readonly AccountRow[];
  reduced: boolean;
  progress: MotionValue<number>;
}): React.JSX.Element {
  const brief = step === 'brief';
  const [elapsed, setElapsed] = React.useState(progress.get());
  const phase = useMotionValue(brief ? 1 : 0);
  const canvasRef = React.useRef<HTMLDivElement>(null);
  const originRef = React.useRef<HTMLDivElement>(null);
  const targetRef = React.useRef<HTMLDivElement>(null);
  const [anchors, setAnchors] = React.useState({
    originX: 0,
    originY: 0,
    targetX: 0,
    targetY: 0
  });
  const { count, settled } = useDiscovery(reduced, brief, BRIEF_ARRIVALS);

  useMotionValueEvent(progress, 'change', (value) => {
    if (!brief) setElapsed((current) => Math.max(current, value));
  });

  React.useEffect(() => {
    const playback = animate(phase, brief ? 1 : 0, {
      duration: reduced ? 0 : 0.85,
      ease: [0.22, 1, 0.36, 1]
    });
    return () => playback.stop();
  }, [brief, phase, reduced]);

  React.useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const origin = originRef.current;
    const target = targetRef.current;
    if (!canvas || !origin || !target) return;
    const position = (element: HTMLElement): { x: number; y: number } => {
      let x = 0;
      let y = 0;
      let current: HTMLElement | null = element;
      while (current && current !== canvas) {
        x += current.offsetLeft;
        y += current.offsetTop;
        current = current.offsetParent as HTMLElement | null;
      }
      return { x, y };
    };
    const measure = (): void => {
      const from = position(origin);
      const to = position(target);
      setAnchors({
        originX: from.x,
        originY: from.y,
        targetX: to.x,
        targetY: to.y
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(canvas);
    observer.observe(origin);
    observer.observe(target);
    if (origin.parentElement) observer.observe(origin.parentElement);
    return () => observer.disconnect();
  }, []);

  const identityX = useTransform(
    phase,
    (value) => anchors.originX + (anchors.targetX - anchors.originX) * value
  );
  const identityY = useTransform(
    phase,
    (value) => anchors.originY + (anchors.targetY - anchors.originY) * value
  );
  const promptY = useTransform(
    phase,
    (value) => (anchors.targetY - anchors.originY) * value
  );
  const promptScale = useTransform(phase, [0, 1], [1, 0.16]);
  const promptOpacity = useTransform(phase, [0, 0.7, 1], [1, 0.45, 0]);
  const textOpacity = useTransform(phase, [0, 0.35], [1, 0]);
  const headerOpacity = useTransform(phase, [0, 0.2, 1], [0, 0.7, 1]);
  const typedFraction = Math.min(1, Math.max(0, (elapsed - 0.05) / 0.6));
  const visibleChars = reduced
    ? ACCOUNT_QUESTION_TEXT.length
    : Math.floor(typedFraction * ACCOUNT_QUESTION_TEXT.length);
  const isTyping = visibleChars < ACCOUNT_QUESTION_TEXT.length;

  return (
    <AccountFocusSurface reduced={reduced}>
      <div
        ref={canvasRef}
        data-account-research={step}
        className="relative z-10 h-[412px] w-full max-w-[760px] md:h-[324px]"
      >
        <div
          aria-hidden={!brief}
          inert={!brief}
          className="absolute inset-0"
        >
          <motion.div style={{ opacity: headerOpacity }}>
            <AccountSceneHeader
              group={false}
              count={count}
              identityRef={targetRef}
              hideIdentity
            />
          </motion.div>
          <div className="relative">
            <motion.span
              aria-hidden
              animate={{ opacity: brief ? 1 : 0 }}
              transition={{ duration: reduced ? 0 : 0.4 }}
              className="absolute bottom-10 left-[19px] top-10 w-px bg-teal-500/20 md:left-[21px]"
            />
            {rows.slice(0, 3).map((row, index) => (
              <motion.article
                key={row.label}
                data-account-focus
                data-account-signal={index + 1}
                initial={false}
                animate={{ opacity: brief ? 1 : 0, y: brief ? 0 : 12 }}
                transition={{
                  duration: reduced ? 0 : 0.5,
                  delay: reduced || !brief ? 0 : BRIEF_ARRIVALS[index],
                  ease: [0.22, 1, 0.36, 1]
                }}
                className="group relative flex min-h-[100px] items-center gap-3 border-b border-[color:var(--hairline-2)] py-4 last:border-b-0 md:min-h-[84px] md:gap-4 md:py-3"
              >
                <motion.span
                  aria-hidden
                  initial={false}
                  animate={{
                    opacity: brief && !settled && count === index + 1 ? 1 : 0
                  }}
                  transition={{ duration: reduced ? 0 : 0.35 }}
                  className="pointer-events-none absolute -inset-x-3 inset-y-1 rounded-[12px] bg-[color:var(--mint)]/65"
                />
                <span
                  data-account-anchor
                  className="relative z-10 grid size-10 shrink-0 place-items-center rounded-[12px] border border-teal-500/20 bg-[color:var(--paper)] text-[color:var(--accent-darker)] transition-colors duration-200 group-hover:bg-[color:var(--mint)] md:size-11"
                >
                  <SourceMark index={index} />
                </span>
                <div className="relative min-w-0 flex-1">
                  <p className="text-[13px] font-semibold leading-snug tracking-[-0.01em] text-[color:var(--ink)] md:text-[15px]">
                    {row.label}
                  </p>
                  <p className="mt-1 hidden text-[11px] leading-snug text-[color:var(--ink-4)] md:block">
                    {row.detail}
                  </p>
                  <p className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[10px] leading-snug text-[color:var(--ink-4)]">
                    <span>{SOURCES[index]}</span>
                    <span
                      aria-hidden
                      className="text-teal-600/55"
                    >
                      ·
                    </span>
                    <span className="tabular-nums">
                      {row.value === 'Today' ? 'Today' : row.value + ' ago'}
                    </span>
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>

        <div
          aria-hidden={brief}
          inert={brief}
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <div className="relative w-full max-w-[680px] p-5 md:p-6">
            <motion.div
              data-account-prompt-surface
              aria-hidden
              style={{
                y: promptY,
                scaleY: promptScale,
                opacity: promptOpacity,
                transformOrigin: '50% 0%'
              }}
              className="surface-card absolute inset-0 rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--paper)]"
            />
            <div
              ref={originRef}
              aria-hidden
              className="relative mb-4 h-8"
            />
            <motion.div
              style={{ opacity: textOpacity, y: promptY }}
              className="relative"
            >
              <div
                role="textbox"
                aria-label="Example account research request"
                aria-readonly="true"
                aria-multiline="true"
                className="h-[176px] overflow-hidden px-0.5 font-mono text-[14px] leading-[1.75] text-[color:var(--ink)] sm:h-[120px] md:h-[104px] md:text-[17px]"
              >
                <span className="sr-only">{ACCOUNT_QUESTION_TEXT}</span>
                <span aria-hidden>
                  <TypedPrompt
                    prompt={ACCOUNT_QUESTION}
                    typedChars={visibleChars}
                    isTyping={isTyping && !brief}
                  />
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          data-account-carried-identity
          style={{ x: identityX, y: identityY }}
          className="pointer-events-none absolute left-0 top-0 z-20 flex items-center gap-2.5"
        >
          <AccountMark />
          <div>
            <p className="text-[13px] font-semibold tracking-[-0.015em] text-[color:var(--ink)] md:text-[14px]">
              Quillstack
            </p>
            {brief && (
              <p className="mt-0.5 text-[10px] leading-none text-[color:var(--ink-4)]">
                Example
                <span className="hidden min-[375px]:inline"> account</span>
              </p>
            )}
          </div>
        </motion.div>
      </div>
    </AccountFocusSurface>
  );
}

function useDiscovery(
  reduced: boolean,
  enabled = true,
  arrivals: readonly number[] = ARRIVAL_SECONDS
): { count: number; settled: boolean } {
  const [count, setCount] = React.useState(reduced ? 3 : 0);
  const [settled, setSettled] = React.useState(reduced);

  React.useEffect(() => {
    if (reduced || !enabled) return;
    setCount(0);
    setSettled(false);
    const timers = arrivals.map((delay, index) =>
      window.setTimeout(() => setCount(index + 1), (delay + 0.2) * 1000)
    );
    timers.push(window.setTimeout(() => setSettled(true), 2600));
    return () => timers.forEach(window.clearTimeout);
  }, [arrivals, enabled, reduced]);

  return !enabled
    ? { count: 0, settled: false }
    : reduced
      ? { count: 3, settled: true }
      : { count, settled };
}

function AccountSceneHeader({
  group,
  count,
  identityRef,
  hideIdentity = false
}: {
  group: boolean;
  count: number;
  identityRef?: React.Ref<HTMLDivElement>;
  hideIdentity?: boolean;
}): React.JSX.Element {
  return (
    <div className="mb-3 flex items-center justify-between gap-3 border-b border-[color:var(--hairline)] pb-4 md:mb-2">
      <div
        ref={identityRef}
        aria-hidden={hideIdentity || undefined}
        className={`flex min-w-0 items-center gap-2.5 ${hideIdentity ? 'invisible' : ''}`}
      >
        <AccountMark />
        <div>
          <p className="text-[13px] font-semibold tracking-[-0.015em] text-[color:var(--ink)] md:text-[14px]">
            Quillstack
          </p>
          <p className="mt-0.5 text-[10px] leading-none text-[color:var(--ink-4)]">
            Example<span className="hidden min-[375px]:inline"> account</span>
          </p>
        </div>
      </div>
      <div className="shrink-0 text-right">
        <p className="flex items-center justify-end gap-1.5 text-[10px] font-medium text-[color:var(--accent-darker)] md:text-[11px]">
          <span
            aria-hidden
            className="size-1.5 rounded-full bg-[color:var(--accent-deep)]"
          />
          {group ? 'Buying group' : 'Bubbling up'}
        </p>
        <p className="mt-1 font-mono text-[9px] tabular-nums text-[color:var(--ink-4)] md:text-[10px]">
          <span className="min-[375px]:hidden">
            {count}{' '}
            {group
              ? count === 1
                ? 'person'
                : 'people'
              : count === 1
                ? 'signal'
                : 'signals'}
          </span>
          <span className="hidden min-[375px]:inline">
            {count}{' '}
            {group
              ? count === 1
                ? 'person mapped'
                : 'people mapped'
              : count === 1
                ? 'signal connected'
                : 'signals connected'}
          </span>
        </p>
      </div>
    </div>
  );
}

export function SourceMark({ index }: { index: number }): React.JSX.Element {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      className="size-[18px]"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.35"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {index === 0 ? (
        <>
          <circle
            cx="9"
            cy="7"
            r="3"
          />
          <path d="M3 20v-2a6 6 0 0 1 12 0v2M18 7v6m-3-3h6" />
        </>
      ) : index === 1 ? (
        <>
          <rect
            x="3"
            y="4"
            width="18"
            height="16"
            rx="2"
          />
          <path d="M3 9h18M7 6.5h.01M10 6.5h.01M7 13h4M7 16h8" />
        </>
      ) : (
        <>
          <path d="M5 8h13l-3-3m3 3-3 3M19 16H6l3 3m-3-3 3-3" />
          <path
            d="M3 4v16M21 4v16"
            opacity=".45"
          />
        </>
      )}
    </svg>
  );
}

function DemoPortrait({
  src,
  name
}: {
  src: string;
  name: string;
}): React.JSX.Element {
  const [failed, setFailed] = React.useState(false);
  return (
    <span
      data-account-anchor
      className="relative grid size-[52px] shrink-0 place-items-center overflow-hidden rounded-full bg-[color:var(--mint)] font-mono text-[12px] font-medium text-[color:var(--accent-darker)] ring-1 ring-[color:var(--hairline)] ring-offset-[3px] ring-offset-[color:var(--paper)]"
    >
      {name
        .split(' ')
        .map((part) => part[0])
        .slice(0, 2)
        .join('')}
      {!failed && (
        <img
          src={src}
          alt=""
          width={52}
          height={52}
          onError={() => setFailed(true)}
          className="absolute inset-0 size-full object-cover saturate-[0.8]"
        />
      )}
    </span>
  );
}

export function AccountBuyingGroupScene({
  rows,
  reduced
}: {
  rows: readonly AccountRow[];
  reduced: boolean;
}): React.JSX.Element {
  const { count } = useDiscovery(reduced);
  const graphRef = React.useRef<HTMLDivElement>(null);
  const [graphWidth, setGraphWidth] = React.useState(760);

  React.useEffect(() => {
    const graph = graphRef.current;
    if (!graph) return;
    const measure = (): void => setGraphWidth(graph.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(graph);
    return () => observer.disconnect();
  }, []);

  return (
    <AccountFocusSurface reduced={reduced}>
      <div className="relative z-10 w-full max-w-[760px]">
        <AccountSceneHeader
          group
          count={count}
        />
        <div
          ref={graphRef}
          className="relative grid gap-1 py-2 md:h-[288px] md:block md:py-0"
        >
          <span
            aria-hidden
            className="absolute bottom-12 left-[26px] top-12 w-px bg-teal-500/20 md:hidden"
          />
          <svg
            aria-hidden
            viewBox={`0 0 ${graphWidth} 288`}
            preserveAspectRatio="none"
            className="absolute inset-0 hidden size-full overflow-visible md:block"
          >
            {PEOPLE.map((person, index) => (
              <motion.path
                key={person.photo}
                d={`M${(graphWidth * ACCOUNT_NODE.x) / 100} ${ACCOUNT_NODE.y} C${(graphWidth * ACCOUNT_NODE.x) / 100} ${(ACCOUNT_NODE.y + person.y) / 2} ${(graphWidth * person.x) / 100} ${(ACCOUNT_NODE.y + person.y) / 2} ${(graphWidth * person.x) / 100} ${person.y}`}
                fill="none"
                stroke="var(--accent-deep)"
                strokeWidth="1.25"
                vectorEffect="non-scaling-stroke"
                initial={reduced ? false : { pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.3 }}
                transition={{
                  duration: reduced ? 0 : 0.6,
                  delay: reduced ? 0 : ARRIVAL_SECONDS[index] - 0.25,
                  ease: [0.22, 0.68, 0.24, 1]
                }}
              />
            ))}
          </svg>
          <div
            aria-label="Quillstack account"
            className="absolute hidden md:block"
            style={{ left: `${ACCOUNT_NODE.x}%`, top: ACCOUNT_NODE.y }}
          >
            <motion.div
              initial={reduced ? false : { opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', stiffness: 220, damping: 24 }}
              className="-ml-7 -mt-7"
            >
              <AccountMark large />
            </motion.div>
          </div>
          {rows.slice(0, 3).map((person, index) => {
            const node = PEOPLE[index]!;
            return (
              <div
                key={person.label}
                className="relative md:absolute md:left-[var(--node-x)] md:top-[var(--node-y)]"
                style={
                  {
                    '--node-x': `${node.x}%`,
                    '--node-y': `${node.y}px`
                  } as React.CSSProperties
                }
              >
                <motion.article
                  data-account-focus
                  initial={reduced ? false : { opacity: 0, scale: 0.94, y: 12 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{
                    type: 'spring',
                    stiffness: 210,
                    damping: 25,
                    delay: reduced ? 0 : ARRIVAL_SECONDS[index]
                  }}
                  className="relative flex min-h-[94px] items-center gap-3 border-b border-[color:var(--hairline-2)] py-4 md:-ml-[26px] md:-mt-[26px] md:min-h-0 md:w-[198px] md:items-start md:border-b-0 md:py-0"
                >
                  <DemoPortrait
                    src={node.photo}
                    name={person.label}
                  />
                  <div className="min-w-0 pt-0.5">
                    <p className="whitespace-nowrap text-[13px] font-semibold leading-tight tracking-[-0.015em] text-[color:var(--ink)] md:text-[14px]">
                      {person.label}
                    </p>
                    <p className="mt-1 text-[11px] font-medium leading-tight text-[color:var(--accent-darker)]">
                      {person.value}
                    </p>
                    <p className="mt-1.5 max-w-[150px] text-[10px] leading-snug text-[color:var(--ink-4)]">
                      {node.reason}
                    </p>
                  </div>
                </motion.article>
              </div>
            );
          })}
        </div>
      </div>
    </AccountFocusSurface>
  );
}

// Focus follows the source or person being inspected, and disappears on empty canvas.
function AccountFocusSurface({
  children,
  reduced
}: {
  children: React.ReactNode;
  reduced: boolean;
}): React.JSX.Element {
  const x = useSpring(0, { stiffness: 300, damping: 28 });
  const y = useSpring(0, { stiffness: 300, damping: 28 });
  const scale = useSpring(1, { stiffness: 300, damping: 28 });
  const shown = React.useRef(false);
  const [visible, setVisible] = React.useState(false);

  const clear = (): void => {
    shown.current = false;
    setVisible(false);
  };
  const focusPointer = (event: React.PointerEvent<HTMLElement>): void => {
    if (reduced || event.pointerType !== 'mouse') return;
    const target = (event.target as Element).closest('[data-account-focus]');
    const anchor = target?.querySelector('[data-account-anchor]');
    if (!anchor) {
      clear();
      return;
    }
    const surface = event.currentTarget.getBoundingClientRect();
    const rect = anchor.getBoundingClientRect();
    const ratioX = event.currentTarget.clientWidth / surface.width;
    const ratioY = event.currentTarget.clientHeight / surface.height;
    const nextX = (rect.left + rect.width / 2 - surface.left) * ratioX - 32;
    const nextY = (rect.top + rect.height / 2 - surface.top) * ratioY - 32;
    const nextScale = (rect.width * ratioX + 14) / 64;
    if (!shown.current) {
      x.jump(nextX);
      y.jump(nextY);
      scale.jump(nextScale);
    } else {
      x.set(nextX);
      y.set(nextY);
      scale.set(nextScale);
    }
    shown.current = true;
    setVisible(true);
  };

  return (
    <section
      onPointerMove={focusPointer}
      onPointerLeave={clear}
      className="relative flex h-full min-h-0 items-center justify-center overflow-hidden px-5 py-6 md:px-10"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-35 [background-image:radial-gradient(circle,rgba(36,166,137,0.16)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_72%)]"
      />
      {!reduced && (
        <motion.span
          aria-hidden
          animate={{ opacity: visible ? 1 : 0 }}
          transition={{ duration: 0.18 }}
          style={{ x, y, scale }}
          className="pointer-events-none absolute left-0 top-0 z-20 hidden size-16 rounded-full border border-teal-500/35 shadow-[0_0_0_4px_rgba(36,166,137,0.055)] md:block"
        />
      )}
      {children}
    </section>
  );
}
